/**
 * Life statistics display component
 */

import { LifeStats, formatNumber, formatPercent, formatCompactNumber } from '../utils/calculations';
import { LifeExpectancyData, DATA_SOURCES, formatCitation } from '../data/lifeExpectancy';

export interface LifeStatsOptions {
    stats: LifeStats;
    expectancyData: LifeExpectancyData;
    lifeExpectancy: number;
    isCustomExpectancy: boolean;
}

/**
 * Create the main statistics cards
 */
export function createStatsCards(options: LifeStatsOptions): HTMLElement {
    const { stats } = options;

    const container = document.createElement('div');
    container.className = 'stats-grid';

    const statsData = [
        {
            value: formatNumber(stats.weeksLived),
            label: 'Weeks lived',
            accent: true
        },
        {
            value: formatNumber(stats.weeksRemaining),
            label: 'Weeks remaining (est.)',
            accent: false
        },
        {
            value: formatPercent(stats.percentLived),
            label: 'Of expected life',
            accent: false
        },
        {
            value: `${stats.ageYears}y ${stats.ageMonths}m`,
            label: 'Your age',
            accent: false
        }
    ];

    statsData.forEach(stat => {
        const card = document.createElement('div');
        card.className = 'stat-card glass-card';
        card.innerHTML = `
      <div class="stat-value ${stat.accent ? 'accent' : ''}">${stat.value}</div>
      <div class="stat-label">${stat.label}</div>
    `;
        container.appendChild(card);
    });

    return container;
}

/**
 * Create the reflective statistics section
 */
export function createReflectiveStats(stats: LifeStats): HTMLElement {
    const container = document.createElement('div');
    container.className = 'reflective-stats glass-card';

    const title = document.createElement('h3');
    title.textContent = 'In your lifetime so far...';
    container.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'reflective-grid';

    const reflectiveData = [
        {
            value: formatCompactNumber(stats.heartbeats),
            label: 'Heartbeats',
            icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 21C12 21 4 14 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.09C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 14 16 21 16 21" fill="var(--accent)" opacity="0.2"/>
                <path d="M19.5 12.572L12 20l-7.5-7.428A5 5 0 1 1 12 6.006a5 5 0 1 1 7.5 6.572"/>
            </svg>`
        },
        {
            value: formatCompactNumber(stats.breaths),
            label: 'Breaths taken',
            icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 22c-4-3-8-6-8-10a8 8 0 0 1 16 0c0 4-4 7-8 10Z" fill="var(--accent)" opacity="0.15"/>
                <path d="M12 2v4m0 4v4m-4-6h8"/>
            </svg>`
        },
        {
            value: formatNumber(stats.sunrises),
            label: 'Sunrises witnessed',
            icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle class="sunrise-sun" cx="12" cy="17" r="5" fill="var(--accent)" opacity="0.3"/>
                <path d="M12 2v2m0 18v-4m-7.07-7.07l1.41 1.41m12.73 0l1.41-1.41M2 17h4m12 0h4M6.34 6.34l1.41 1.41m9.9 0l1.41-1.41"/>
                <line x1="4" y1="22" x2="20" y2="22"/>
            </svg>`
        },
        {
            value: formatNumber(stats.fullMoons),
            label: 'Full moons seen',
            icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="9" fill="rgba(255,255,255,0.8)"/>
                <circle cx="12" cy="12" r="9"/>
            </svg>`
        }
    ];

    reflectiveData.forEach(item => {
        const element = document.createElement('div');
        element.className = 'reflective-item';
        element.innerHTML = `
      <div class="reflective-icon">${item.icon}</div>
      <div class="reflective-value">${item.value}</div>
      <div class="reflective-label">${item.label}</div>
    `;
        grid.appendChild(element);
    });

    container.appendChild(grid);
    return container;
}

/**
 * Create the quote section
 */
export function createQuoteSection(): HTMLElement {
    const quotes = [
        {
            text: '"The purpose of life is not to be happy. It is to be useful, to be honorable, to be compassionate, to have it make some difference that you have lived and lived well."',
            author: 'Ralph Waldo Emerson'
        },
        {
            text: '"Life is what happens when you\'re busy making other plans."',
            author: 'John Lennon'
        },
        {
            text: '"In the end, it\'s not the years in your life that count. It\'s the life in your years."',
            author: 'Abraham Lincoln'
        },
        {
            text: '"The two most important days in your life are the day you are born and the day you find out why."',
            author: 'Mark Twain'
        },
        {
            text: '"You can\'t go back and change the beginning, but you can start where you are and change the ending."',
            author: 'C.S. Lewis'
        },
        {
            text: '"Yesterday is history, tomorrow is a mystery, but today is a gift. That is why it is called the present."',
            author: 'Master Oogway'
        },
        {
            text: '"Do not dwell in the past, do not dream of the future, concentrate the mind on the present moment."',
            author: 'Buddha'
        },
        {
            text: '"Enjoy the little things, for one day you may look back and realize they were the big things."',
            author: 'Robert Brault'
        },
        {
            text: '"Life is not measured by the number of breaths we take, but by the moments that take our breath away."',
            author: 'Maya Angelou'
        },
        {
            text: '"Your time is limited, so don\'t waste it living someone else\'s life."',
            author: 'Steve Jobs'
        },
        {
            text: '"The best time to plant a tree was 20 years ago. The second best time is now."',
            author: 'Chinese Proverb'
        },
        {
            text: '"It is not death that a man should fear, but he should fear never beginning to live."',
            author: 'Marcus Aurelius'
        },
        {
            text: '"Dost thou love life? Then do not squander time, for that\'s the stuff life is made of."',
            author: 'Benjamin Franklin'
        },
        {
            text: '"Life is really simple, but we insist on making it complicated."',
            author: 'Confucius'
        },
        {
            text: '"Happiness depends upon ourselves."',
            author: 'Aristotle'
        },
        {
            text: '"It is not how much we have, but how much we enjoy, that makes happiness."',
            author: 'Charles Spurgeon'
        },
        {
            text: '"The only way to do great work is to love what you do."',
            author: 'Steve Jobs'
        },
        {
            text: '"Life is extremely short and anxious for those who forget the past, neglect the present, and fear the future."',
            author: 'Seneca'
        },
        {
            text: '"We have two lives, and the second begins when we realize we only have one."',
            author: 'Confucius'
        },
        {
            text: '"Turning your back on the darkness is not the same as looking for the light."',
            author: 'Unknown'
        },
        {
            text: '"Live as if you were to die tomorrow. Learn as if you were to live forever."',
            author: 'Mahatma Gandhi'
        },
        {
            text: '"The journey of a thousand miles begins with one step."',
            author: 'Lao Tzu'
        },
        {
            text: '"It always seems impossible until it\'s done."',
            author: 'Nelson Mandela'
        },
        {
            text: '"Act as if what you do makes a difference. It does."',
            author: 'William James'
        },
        {
            text: '"Keep your face always toward the sunshine—and shadows will fall behind you."',
            author: 'Walt Whitman'
        }
    ];

    // Pick a random quote
    const quote = quotes[Math.floor(Math.random() * quotes.length)];

    const container = document.createElement('div');
    container.className = 'quote-section';
    container.innerHTML = `
    <p class="quote-text">${quote.text}</p>
    <p class="quote-attribution">— ${quote.author}</p>
  `;

    return container;
}

/**
 * Create the data source citation
 */
export function createDataSource(options: LifeStatsOptions): HTMLElement {
    const { expectancyData, lifeExpectancy, isCustomExpectancy } = options;
    const source = DATA_SOURCES[expectancyData.source];

    const container = document.createElement('div');
    container.className = 'data-source';

    if (isCustomExpectancy) {
        container.innerHTML = `
      <p>
        Using your custom life expectancy of <strong>${lifeExpectancy} years</strong>.
        <br/>
        Statistical data from <a href="${source.url}" target="_blank" rel="noopener">${formatCitation(expectancyData)}</a>
      </p>
    `;
    } else {
        container.innerHTML = `
      <p>
        Life expectancy data: <a href="${source.url}" target="_blank" rel="noopener">${formatCitation(expectancyData)}</a>
      </p>
    `;
    }

    return container;
}

/**
 * Create the footer with actions
 */
export function createVizFooter(onStartOver: () => void, onShare?: () => void): HTMLElement {
    const container = document.createElement('div');
    container.className = 'viz-footer';

    // Share button (if callback provided)
    if (onShare) {
        const shareBtn = document.createElement('button');
        shareBtn.className = 'btn btn-primary';
        shareBtn.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
            </svg>
            <span>Share</span>
        `;
        shareBtn.addEventListener('click', onShare);
        container.appendChild(shareBtn);
    }

    const button = document.createElement('button');
    button.className = 'btn btn-secondary';
    button.textContent = 'Start over';
    button.addEventListener('click', onStartOver);

    container.appendChild(button);
    return container;
}
